import {
  API_LIMITS,
  appVersionSchema,
  type ClientLearningEvent,
  clientLearningEventSchema,
  contentVersionSchema,
  courseCodeSchema,
  type EventBatchRequest,
  type EventBatchResponse,
  eventBatchRequestSchema,
  eventBatchResponseSchema,
  type TaskAttemptRequest,
  type TaskAttemptResponse,
  taskAttemptRequestSchema,
  taskAttemptResponseSchema,
  timestampSchema,
  uuidSchema,
} from "@history-sim/api-contracts";
import { type DBSchema, type IDBPDatabase, openDB } from "idb";

const DATABASE_VERSION = 3;
const DEFAULT_DATABASE_NAME = "history-sim-learning-sync-v1";
const DEFAULT_CAPACITY = 5_000;
const SAFE_REASON_CODE = /^[A-Z][A-Z0-9_]{2,63}$/u;

export interface SyncSessionMapping {
  readonly clientSessionId: string;
  readonly serverSessionId: string;
  readonly participantId: string;
  readonly enrollmentId: string;
  readonly classroomId: string;
  readonly classroomName: string;
  readonly courseCode: string;
  readonly contentVersion: string;
  readonly appVersion: string;
  readonly startedAt: string;
  readonly updatedAt: string;
}

interface StoredOutboxEvent {
  readonly eventId: string;
  readonly clientSessionId: string;
  readonly event: ClientLearningEvent;
  readonly contentVersion: string;
  readonly appVersion: string;
  readonly enqueuedAt: string;
  readonly batchId?: string;
  readonly attemptCount: number;
  readonly lastAttemptAt?: string;
}

interface DeliveredEventReceipt {
  readonly eventId: string;
  readonly clientSessionId: string;
  readonly deliveredAt: string;
}

interface StoredTaskAttempt {
  readonly clientAttemptId: string;
  readonly clientSessionId: string;
  readonly request: TaskAttemptRequest;
  readonly enqueuedAt: string;
  readonly attemptCount: number;
  readonly lastAttemptAt?: string;
}

export interface DeliveredTaskAttemptReceipt {
  readonly clientAttemptId: string;
  readonly clientSessionId: string;
  readonly response: TaskAttemptResponse;
  readonly deliveredAt: string;
  readonly appliedAt?: string;
  readonly retiredAt?: string;
  readonly retirementReasonCode?: string;
}

export interface DeadLetterTaskAttempt {
  readonly clientAttemptId: string;
  readonly clientSessionId: string;
  readonly request: TaskAttemptRequest;
  readonly failedAt: string;
  readonly reasonCode: string;
}

export interface DeadLetterEvent {
  readonly eventId: string;
  readonly clientSessionId: string;
  readonly event: ClientLearningEvent;
  readonly contentVersion: string;
  readonly appVersion: string;
  readonly failedAt: string;
  readonly reasonCode: string;
}

interface LearningSyncDatabase extends DBSchema {
  events: {
    key: string;
    value: StoredOutboxEvent;
    indexes: { "by-session": string };
  };
  sessions: {
    key: string;
    value: SyncSessionMapping;
  };
  deadLetters: {
    key: string;
    value: DeadLetterEvent;
    indexes: { "by-session": string };
  };
  delivered: {
    key: string;
    value: DeliveredEventReceipt;
    indexes: { "by-session": string };
  };
  taskAttempts: {
    key: string;
    value: StoredTaskAttempt;
    indexes: { "by-session": string };
  };
  deliveredTaskAttempts: {
    key: string;
    value: DeliveredTaskAttemptReceipt;
    indexes: { "by-session": string };
  };
  deadLetterTaskAttempts: {
    key: string;
    value: DeadLetterTaskAttempt;
    indexes: { "by-session": string };
  };
}

export interface ClaimedEventBatch {
  readonly clientSessionId: string;
  readonly request: EventBatchRequest;
}

export interface QueuedClientLearningEvent {
  readonly event: ClientLearningEvent;
  readonly contentVersion: string;
  readonly appVersion: string;
}

export interface RetiredContentVersionRecords {
  readonly sessionMappings: number;
  readonly pendingEvents: number;
  readonly pendingTaskAttempts: number;
  readonly deliveredTaskAttempts: number;
}

export interface ClaimedTaskAttempt {
  readonly clientSessionId: string;
  readonly request: TaskAttemptRequest;
}

export class LearningOutboxCapacityError extends Error {
  constructor() {
    super("Learning event outbox capacity has been reached");
    this.name = "LearningOutboxCapacityError";
  }
}

export interface IndexedDbLearningOutboxOptions {
  readonly databaseName?: string;
  readonly capacity?: number;
  readonly now?: () => Date;
  readonly id?: () => string;
}

function positiveCapacity(value: number | undefined): number {
  const candidate = value ?? DEFAULT_CAPACITY;
  if (!Number.isSafeInteger(candidate) || candidate < API_LIMITS.eventBatch || candidate > 50_000) {
    throw new Error(`Outbox capacity must be between ${API_LIMITS.eventBatch} and 50000`);
  }
  return candidate;
}

function exactIdSet(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length) return false;
  const expected = new Set(left);
  return expected.size === right.length && right.every((value) => expected.has(value));
}

export class IndexedDbLearningOutbox {
  readonly #databaseName: string;
  readonly #capacity: number;
  readonly #now: () => Date;
  readonly #id: () => string;
  #database: Promise<IDBPDatabase<LearningSyncDatabase>> | undefined;

  constructor(options: IndexedDbLearningOutboxOptions = {}) {
    const databaseName = options.databaseName?.trim() || DEFAULT_DATABASE_NAME;
    if (!/^[A-Za-z0-9][A-Za-z0-9._-]{2,127}$/u.test(databaseName)) {
      throw new Error("IndexedDB outbox database name is invalid");
    }
    this.#databaseName = databaseName;
    this.#capacity = positiveCapacity(options.capacity);
    this.#now = options.now ?? (() => new Date());
    this.#id = options.id ?? (() => crypto.randomUUID());
  }

  async putSession(mapping: SyncSessionMapping): Promise<void> {
    const normalized = {
      ...mapping,
      clientSessionId: uuidSchema.parse(mapping.clientSessionId),
      serverSessionId: uuidSchema.parse(mapping.serverSessionId),
      participantId: uuidSchema.parse(mapping.participantId),
      enrollmentId: uuidSchema.parse(mapping.enrollmentId),
      classroomId: uuidSchema.parse(mapping.classroomId),
      classroomName: mapping.classroomName.trim(),
      courseCode: courseCodeSchema.parse(mapping.courseCode),
      contentVersion: contentVersionSchema.parse(mapping.contentVersion),
      appVersion: appVersionSchema.parse(mapping.appVersion),
      startedAt: timestampSchema.parse(mapping.startedAt),
      updatedAt: timestampSchema.parse(mapping.updatedAt),
    };
    if (!normalized.classroomName || normalized.classroomName.length > API_LIMITS.className) {
      throw new Error("Sync session classroom name is invalid");
    }
    const database = await this.#open();
    await database.put("sessions", normalized);
  }

  async getSession(clientSessionId: string): Promise<SyncSessionMapping | undefined> {
    const database = await this.#open();
    return database.get("sessions", uuidSchema.parse(clientSessionId));
  }

  async retireIncompatibleContentVersions(
    activeContentVersion: string,
  ): Promise<RetiredContentVersionRecords> {
    const contentVersion = contentVersionSchema.parse(activeContentVersion);
    const retiredAt = this.#now().toISOString();
    const reasonCode = "CONTENT_VERSION_RETIRED";
    const database = await this.#open();
    const transaction = database.transaction(
      [
        "events",
        "deadLetters",
        "taskAttempts",
        "deadLetterTaskAttempts",
        "sessions",
        "deliveredTaskAttempts",
      ],
      "readwrite",
    );
    const [events, taskAttempts, sessions, deliveredTaskAttempts] = await Promise.all([
      transaction.objectStore("events").getAll(),
      transaction.objectStore("taskAttempts").getAll(),
      transaction.objectStore("sessions").getAll(),
      transaction.objectStore("deliveredTaskAttempts").getAll(),
    ]);

    let retiredEvents = 0;
    for (const record of events) {
      if (record.contentVersion === contentVersion) continue;
      await transaction.objectStore("deadLetters").put({
        eventId: record.eventId,
        clientSessionId: record.clientSessionId,
        event: record.event,
        contentVersion: record.contentVersion,
        appVersion: record.appVersion,
        failedAt: retiredAt,
        reasonCode,
      });
      await transaction.objectStore("events").delete(record.eventId);
      retiredEvents += 1;
    }

    let retiredTaskAttempts = 0;
    for (const record of taskAttempts) {
      if (record.request.contentVersion === contentVersion) continue;
      await transaction.objectStore("deadLetterTaskAttempts").put({
        clientAttemptId: record.clientAttemptId,
        clientSessionId: record.clientSessionId,
        request: record.request,
        failedAt: retiredAt,
        reasonCode,
      });
      await transaction.objectStore("taskAttempts").delete(record.clientAttemptId);
      retiredTaskAttempts += 1;
    }

    let retiredSessions = 0;
    for (const mapping of sessions) {
      if (mapping.contentVersion === contentVersion) continue;
      await transaction.objectStore("sessions").delete(mapping.clientSessionId);
      retiredSessions += 1;
    }

    let retiredReceipts = 0;
    for (const receipt of deliveredTaskAttempts) {
      if (
        receipt.response.contentVersion === contentVersion ||
        receipt.appliedAt !== undefined ||
        receipt.retiredAt !== undefined
      ) {
        continue;
      }
      await transaction.objectStore("deliveredTaskAttempts").put({
        ...receipt,
        retiredAt,
        retirementReasonCode: reasonCode,
      });
      retiredReceipts += 1;
    }

    await transaction.done;
    return {
      sessionMappings: retiredSessions,
      pendingEvents: retiredEvents,
      pendingTaskAttempts: retiredTaskAttempts,
      deliveredTaskAttempts: retiredReceipts,
    };
  }

  async enqueue(
    clientSessionId: string,
    events: readonly QueuedClientLearningEvent[],
  ): Promise<number> {
    const sessionId = uuidSchema.parse(clientSessionId);
    const parsedEvents = events.map((item) => ({
      event: clientLearningEventSchema.parse(item.event),
      contentVersion: contentVersionSchema.parse(item.contentVersion),
      appVersion: appVersionSchema.parse(item.appVersion),
    }));
    const uniqueEvents = [
      ...new Map(parsedEvents.map((item) => [item.event.eventId, item])).values(),
    ];
    if (uniqueEvents.length === 0) return 0;

    const database = await this.#open();
    const transaction = database.transaction(["events", "deadLetters", "delivered"], "readwrite");
    const pendingKeys = new Set(await transaction.objectStore("events").getAllKeys());
    const deadLetterKeys = new Set(await transaction.objectStore("deadLetters").getAllKeys());
    const deliveredKeys = new Set(await transaction.objectStore("delivered").getAllKeys());
    const newEvents = uniqueEvents.filter(
      (item) =>
        !pendingKeys.has(item.event.eventId) &&
        !deadLetterKeys.has(item.event.eventId) &&
        !deliveredKeys.has(item.event.eventId),
    );
    if (pendingKeys.size + newEvents.length > this.#capacity) {
      await transaction.done;
      throw new LearningOutboxCapacityError();
    }
    const enqueuedAt = this.#now().toISOString();
    await Promise.all(
      newEvents.map((item) =>
        transaction.objectStore("events").add({
          eventId: item.event.eventId,
          clientSessionId: sessionId,
          event: item.event,
          contentVersion: item.contentVersion,
          appVersion: item.appVersion,
          enqueuedAt,
          attemptCount: 0,
        }),
      ),
    );
    await transaction.done;
    return newEvents.length;
  }

  async enqueueTaskAttempt(clientSessionId: string, input: TaskAttemptRequest): Promise<boolean> {
    const sessionId = uuidSchema.parse(clientSessionId);
    const request = taskAttemptRequestSchema.parse(input);
    const database = await this.#open();
    const transaction = database.transaction(
      ["taskAttempts", "deliveredTaskAttempts", "deadLetterTaskAttempts"],
      "readwrite",
    );
    const key = request.clientAttemptId;
    const [pending, delivered, deadLetter] = await Promise.all([
      transaction.objectStore("taskAttempts").get(key),
      transaction.objectStore("deliveredTaskAttempts").get(key),
      transaction.objectStore("deadLetterTaskAttempts").get(key),
    ]);
    if (pending || delivered || deadLetter) {
      await transaction.done;
      return false;
    }
    const sessionAttemptCount = await transaction
      .objectStore("taskAttempts")
      .index("by-session")
      .count(sessionId);
    if (sessionAttemptCount >= this.#capacity) {
      await transaction.done;
      throw new LearningOutboxCapacityError();
    }
    await transaction.objectStore("taskAttempts").add({
      clientAttemptId: key,
      clientSessionId: sessionId,
      request,
      enqueuedAt: this.#now().toISOString(),
      attemptCount: 0,
    });
    await transaction.done;
    return true;
  }

  async claimTaskAttempt(clientSessionId: string): Promise<ClaimedTaskAttempt | undefined> {
    const sessionId = uuidSchema.parse(clientSessionId);
    const database = await this.#open();
    const transaction = database.transaction("taskAttempts", "readwrite");
    const records = await transaction.store.index("by-session").getAll(sessionId);
    records.sort(
      (left, right) =>
        left.enqueuedAt.localeCompare(right.enqueuedAt) ||
        left.clientAttemptId.localeCompare(right.clientAttemptId),
    );
    const record = records[0];
    if (!record) {
      await transaction.done;
      return undefined;
    }
    await transaction.store.put({
      ...record,
      attemptCount: record.attemptCount + 1,
      lastAttemptAt: this.#now().toISOString(),
    });
    await transaction.done;
    return { clientSessionId: sessionId, request: record.request };
  }

  async acknowledgeTaskAttempt(
    claimed: ClaimedTaskAttempt,
    response: TaskAttemptResponse,
  ): Promise<DeliveredTaskAttemptReceipt> {
    const parsed = taskAttemptResponseSchema.parse(response);
    if (
      parsed.clientAttemptId !== claimed.request.clientAttemptId ||
      parsed.contentVersion !== claimed.request.contentVersion ||
      parsed.taskId !== claimed.request.taskId
    ) {
      throw new Error("Task attempt acknowledgement does not match the claimed request");
    }
    const database = await this.#open();
    const transaction = database.transaction(
      ["taskAttempts", "deliveredTaskAttempts"],
      "readwrite",
    );
    const record = await transaction.objectStore("taskAttempts").get(parsed.clientAttemptId);
    if (!record || record.clientSessionId !== claimed.clientSessionId) {
      await transaction.done;
      throw new Error("Task attempt changed before acknowledgement could be committed");
    }
    const receipt: DeliveredTaskAttemptReceipt = {
      clientAttemptId: parsed.clientAttemptId,
      clientSessionId: claimed.clientSessionId,
      response: parsed,
      deliveredAt: this.#now().toISOString(),
    };
    await transaction.objectStore("deliveredTaskAttempts").put(receipt);
    await transaction.objectStore("taskAttempts").delete(parsed.clientAttemptId);
    await transaction.done;
    return receipt;
  }

  async deadLetterTaskAttempt(claimed: ClaimedTaskAttempt, reasonCode: string): Promise<boolean> {
    if (!SAFE_REASON_CODE.test(reasonCode)) throw new Error("Dead-letter reason code is invalid");
    const database = await this.#open();
    const transaction = database.transaction(
      ["taskAttempts", "deadLetterTaskAttempts"],
      "readwrite",
    );
    const record = await transaction
      .objectStore("taskAttempts")
      .get(claimed.request.clientAttemptId);
    if (!record || record.clientSessionId !== claimed.clientSessionId) {
      await transaction.done;
      return false;
    }
    await transaction.objectStore("deadLetterTaskAttempts").put({
      clientAttemptId: record.clientAttemptId,
      clientSessionId: record.clientSessionId,
      request: record.request,
      failedAt: this.#now().toISOString(),
      reasonCode,
    });
    await transaction.objectStore("taskAttempts").delete(record.clientAttemptId);
    await transaction.done;
    return true;
  }

  async pendingTaskAttemptCount(clientSessionId: string): Promise<number> {
    const database = await this.#open();
    return database.countFromIndex("taskAttempts", "by-session", uuidSchema.parse(clientSessionId));
  }

  async unappliedTaskAttemptReceipts(
    clientSessionId: string,
  ): Promise<readonly DeliveredTaskAttemptReceipt[]> {
    const database = await this.#open();
    const receipts = await database.getAllFromIndex(
      "deliveredTaskAttempts",
      "by-session",
      uuidSchema.parse(clientSessionId),
    );
    return receipts
      .filter((receipt) => receipt.appliedAt === undefined && receipt.retiredAt === undefined)
      .sort(
        (left, right) =>
          left.deliveredAt.localeCompare(right.deliveredAt) ||
          left.clientAttemptId.localeCompare(right.clientAttemptId),
      );
  }

  async markTaskAttemptApplied(clientAttemptId: string): Promise<void> {
    const attemptId = uuidSchema.parse(clientAttemptId);
    const database = await this.#open();
    const transaction = database.transaction("deliveredTaskAttempts", "readwrite");
    const receipt = await transaction.store.get(attemptId);
    if (!receipt) {
      await transaction.done;
      throw new Error("Delivered task attempt receipt is missing");
    }
    if (!receipt.appliedAt) {
      await transaction.store.put({ ...receipt, appliedAt: this.#now().toISOString() });
    }
    await transaction.done;
  }

  async claimBatch(clientSessionId: string): Promise<ClaimedEventBatch | undefined> {
    const sessionId = uuidSchema.parse(clientSessionId);
    const database = await this.#open();
    const transaction = database.transaction("events", "readwrite");
    const records = await transaction.store.index("by-session").getAll(sessionId);
    records.sort(
      (left, right) =>
        left.enqueuedAt.localeCompare(right.enqueuedAt) ||
        left.eventId.localeCompare(right.eventId),
    );
    const retryBatchId = records.find((record) => record.batchId)?.batchId;
    const pending = records.filter((record) => record.batchId === undefined);
    const firstPending = pending[0];
    const candidates = retryBatchId
      ? records.filter((record) => record.batchId === retryBatchId)
      : firstPending
        ? pending
            .filter(
              (record) =>
                record.contentVersion === firstPending.contentVersion &&
                record.appVersion === firstPending.appVersion,
            )
            .slice(0, API_LIMITS.eventBatch)
        : [];
    if (candidates.length === 0) {
      await transaction.done;
      return undefined;
    }

    const batchId = retryBatchId ?? uuidSchema.parse(this.#id());
    const attemptedAt = this.#now().toISOString();
    for (const record of candidates) {
      await transaction.store.put({
        ...record,
        batchId,
        attemptCount: record.attemptCount + 1,
        lastAttemptAt: attemptedAt,
      });
    }
    await transaction.done;

    const first = candidates[0];
    if (!first) return undefined;
    const request = eventBatchRequestSchema.parse({
      batchId,
      contentVersion: first.contentVersion,
      appVersion: first.appVersion,
      events: candidates.map((record) => record.event),
    });
    return { clientSessionId: sessionId, request };
  }

  async acknowledgeBatch(batch: ClaimedEventBatch, response: EventBatchResponse): Promise<void> {
    const parsed = eventBatchResponseSchema.parse(response);
    if (parsed.batchId !== batch.request.batchId) {
      throw new Error("Event batch acknowledgement ID does not match the claimed batch");
    }
    const acknowledgedIds = [...parsed.acceptedEventIds, ...parsed.duplicateEventIds];
    const requestedIds = batch.request.events.map((event) => event.eventId);
    if (!exactIdSet(requestedIds, acknowledgedIds)) {
      throw new Error("Event batch acknowledgement does not cover the exact claimed event set");
    }

    const database = await this.#open();
    const transaction = database.transaction(["events", "delivered"], "readwrite");
    const records: StoredOutboxEvent[] = [];
    for (const eventId of requestedIds) {
      const record = await transaction.objectStore("events").get(eventId);
      if (
        !record ||
        record.clientSessionId !== batch.clientSessionId ||
        record.batchId !== batch.request.batchId
      ) {
        await transaction.done;
        throw new Error("Event batch changed before acknowledgement could be committed");
      }
      records.push(record);
    }
    const deliveredAt = this.#now().toISOString();
    await Promise.all(
      records.flatMap((record) => [
        transaction.objectStore("delivered").put({
          eventId: record.eventId,
          clientSessionId: record.clientSessionId,
          deliveredAt,
        }),
        transaction.objectStore("events").delete(record.eventId),
      ]),
    );
    await transaction.done;
  }

  async deadLetterBatch(batch: ClaimedEventBatch, reasonCode: string): Promise<number> {
    if (!SAFE_REASON_CODE.test(reasonCode)) throw new Error("Dead-letter reason code is invalid");
    const database = await this.#open();
    const transaction = database.transaction(["events", "deadLetters"], "readwrite");
    const failedAt = this.#now().toISOString();
    let moved = 0;
    for (const event of batch.request.events) {
      const record = await transaction.objectStore("events").get(event.eventId);
      if (record?.batchId !== batch.request.batchId) continue;
      await transaction.objectStore("deadLetters").put({
        eventId: record.eventId,
        clientSessionId: record.clientSessionId,
        event: record.event,
        contentVersion: record.contentVersion,
        appVersion: record.appVersion,
        failedAt,
        reasonCode,
      });
      await transaction.objectStore("events").delete(record.eventId);
      moved += 1;
    }
    await transaction.done;
    return moved;
  }

  async pendingCount(clientSessionId?: string): Promise<number> {
    const database = await this.#open();
    return clientSessionId
      ? database.countFromIndex("events", "by-session", uuidSchema.parse(clientSessionId))
      : database.count("events");
  }

  async deadLetters(clientSessionId: string): Promise<readonly DeadLetterEvent[]> {
    const database = await this.#open();
    return database.getAllFromIndex("deadLetters", "by-session", uuidSchema.parse(clientSessionId));
  }

  async deadLetterTaskAttempts(clientSessionId: string): Promise<readonly DeadLetterTaskAttempt[]> {
    const database = await this.#open();
    return database.getAllFromIndex(
      "deadLetterTaskAttempts",
      "by-session",
      uuidSchema.parse(clientSessionId),
    );
  }

  async close(): Promise<void> {
    if (!this.#database) return;
    const database = await this.#database;
    database.close();
    this.#database = undefined;
  }

  async #open(): Promise<IDBPDatabase<LearningSyncDatabase>> {
    this.#database ??= openDB<LearningSyncDatabase>(this.#databaseName, DATABASE_VERSION, {
      upgrade(database, oldVersion) {
        if (oldVersion < 1) {
          const events = database.createObjectStore("events", { keyPath: "eventId" });
          events.createIndex("by-session", "clientSessionId");
          database.createObjectStore("sessions", { keyPath: "clientSessionId" });
          const deadLetters = database.createObjectStore("deadLetters", { keyPath: "eventId" });
          deadLetters.createIndex("by-session", "clientSessionId");
        }
        if (oldVersion < 2) {
          const delivered = database.createObjectStore("delivered", { keyPath: "eventId" });
          delivered.createIndex("by-session", "clientSessionId");
        }
        if (oldVersion < 3) {
          const taskAttempts = database.createObjectStore("taskAttempts", {
            keyPath: "clientAttemptId",
          });
          taskAttempts.createIndex("by-session", "clientSessionId");
          const deliveredTaskAttempts = database.createObjectStore("deliveredTaskAttempts", {
            keyPath: "clientAttemptId",
          });
          deliveredTaskAttempts.createIndex("by-session", "clientSessionId");
          const deadLetterTaskAttempts = database.createObjectStore("deadLetterTaskAttempts", {
            keyPath: "clientAttemptId",
          });
          deadLetterTaskAttempts.createIndex("by-session", "clientSessionId");
        }
      },
    });
    return this.#database;
  }
}
